/**
 * SfxBrowser — reusable browser for the Starter FX Pack (396 effects).
 *
 * Renders a category-filtered grid of sound effects with inline preview
 * playback and one-click "Add to my library" (imports the effect into
 * the current user's `imaging_assets` as a `kind="fx"` asset so the
 * mixer + production suite can consume it like any user-uploaded FX).
 *
 * Reused across two surfaces:
 *   * Imaging Studio → Beds & FX tab
 *   * Production Suite → side panel
 *
 * Props:
 *   onImport?({asset, created}) — invoked after a successful import;
 *                                   parent uses this to refresh its
 *                                   own asset list.
 *   compact?: bool — tighter card layout for narrow side panels.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Play, Pause, Plus, Search, Loader2, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function SfxBrowser({ onImport, compact = false }) {
  const [manifest, setManifest] = useState(null);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [importedIds, setImportedIds] = useState(new Set());
  const [importingId, setImportingId] = useState("");
  const [playingId, setPlayingId] = useState("");
  const audioRef = useRef(null);

  // Fetch manifest once
  useEffect(() => {
    api.get("/sfx/pack?limit=1000").then((r) => setManifest(r.data)).catch(() => {
      toast.error("Couldn't load SFX pack — try again");
    });
  }, []);

  // Load which items this user already imported so the button reads
  // "Added" instead of "+" for repeat visits.
  useEffect(() => {
    let alive = true;
    api.get("/imaging/assets?kind=fx")
      .then((r) => {
        if (!alive) return;
        const ids = new Set((r.data || [])
          .filter((a) => a.source === "sfx_pack" && a.source_item_id)
          .map((a) => a.source_item_id));
        setImportedIds(ids);
      })
      .catch(() => { /* endpoint may not exist yet — soft-fail */ });
    return () => { alive = false; };
  }, []);

  // Cleanup audio on unmount
  useEffect(() => () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* noop */ }
    }
  }, []);

  const items = useMemo(() => {
    if (!manifest) return [];
    let list = manifest.items || [];
    if (category !== "all") list = list.filter((i) => i.category === category);
    if (query.trim()) {
      const needle = query.trim().toLowerCase();
      list = list.filter((i) => i.name.toLowerCase().includes(needle) || i.id.toLowerCase().includes(needle));
    }
    return list;
  }, [manifest, category, query]);

  const preview = (item) => {
    // Stop existing playback + fire the global "another preview started"
    // event so the app-wide NowPreviewingBar releases its lock.
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
    }
    try { window.dispatchEvent(new CustomEvent("bfa:preview-started")); } catch { /* noop */ }

    if (playingId === item.id) {
      setPlayingId("");
      return;
    }
    const url = `${BACKEND}/api/sfx/pack/file/${item.id}`;
    const a = new Audio(url);
    audioRef.current = a;
    setPlayingId(item.id);
    a.play().catch(() => {
      toast.error("Preview failed to play");
      setPlayingId("");
    });
    a.onended = () => setPlayingId((p) => (p === item.id ? "" : p));
  };

  const importItem = async (item) => {
    setImportingId(item.id);
    try {
      const r = await api.post(`/sfx/pack/${item.id}/import`);
      setImportedIds((prev) => new Set(prev).add(item.id));
      const verb = r.data?.created ? "Imported" : "Already in library";
      toast.success(`${verb} — “${item.name}” now in Beds & FX`);
      if (onImport) onImport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImportingId("");
    }
  };

  if (!manifest) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-6 text-center text-ink-muted text-sm" data-testid="sfx-browser-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading Starter FX Pack…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="sfx-browser">
      {/* Header: name + counts */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-amber">
            // {manifest.pack_label}
          </div>
          <div className="text-ink-primary type-list-title">
            {manifest.total} effects · {manifest.categories.length} categories
          </div>
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-[280px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search 396 effects…"
            data-testid="sfx-browser-search"
            className="w-full rounded-md border border-line bg-bg pl-8 pr-3 py-1.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber transition"
          />
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="sfx-browser-categories">
        <button
          type="button"
          onClick={() => setCategory("all")}
          data-testid="sfx-cat-all"
          className={`text-[11px] px-2.5 py-1 rounded-full border font-mono uppercase tracking-wider transition ${
            category === "all"
              ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
              : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
          }`}
        >
          All · {manifest.total}
        </button>
        {manifest.categories.map((c) => (
          <button
            key={c.slug}
            type="button"
            onClick={() => setCategory(c.slug)}
            data-testid={`sfx-cat-${c.slug}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border font-mono uppercase tracking-wider transition ${
              category === c.slug
                ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
            }`}
          >
            {c.label} · {c.count}
          </button>
        ))}
      </div>

      {/* Results */}
      <div
        className={`grid gap-2 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}
        data-testid="sfx-browser-results"
      >
        {items.length === 0 && (
          <div className="col-span-full text-center text-ink-muted text-sm py-6">
            No effects match your search.
          </div>
        )}
        {items.map((item) => {
          const isPlaying = playingId === item.id;
          const isImporting = importingId === item.id;
          const isImported = importedIds.has(item.id);
          return (
            <div
              key={item.id}
              data-testid={`sfx-item-${item.id}`}
              className="flex items-center gap-2 rounded-md border border-line bg-bg-elevated p-2 hover:border-brand-amber/40 transition"
            >
              <button
                type="button"
                onClick={() => preview(item)}
                data-testid={`sfx-preview-${item.id}`}
                aria-label={`${isPlaying ? "Stop" : "Preview"} ${item.name}`}
                className="p-1.5 rounded-md text-brand-amber hover:bg-brand-amber/10 transition"
              >
                {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-ink-primary truncate" title={item.name}>{item.name}</div>
                <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">{item.category}</div>
              </div>
              <button
                type="button"
                onClick={() => !isImported && importItem(item)}
                disabled={isImporting || isImported}
                data-testid={`sfx-import-${item.id}`}
                aria-label={isImported ? `${item.name} already in library` : `Add ${item.name} to library`}
                className={`p-1.5 rounded-md transition ${
                  isImported
                    ? "text-emerald-400 bg-emerald-500/10"
                    : "text-ink-secondary hover:text-brand-amber hover:bg-brand-amber/10 disabled:opacity-60"
                }`}
              >
                {isImporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isImported ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
